import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { arealplanEndringSchema, useArealplan, useUpdateArealplan, ValidationError } from '@origo/planregister';
import { Notice } from '@origo/origo-ui';
import PlanDetailSkeleton from '../../components/PlanDetailSkeleton';
import UnsavedChangesDialog from '../../components/UnsavedChangesDialog';
import { useToast } from '@origo/origo-components';
import PlanInfoPage, { type PlanInfoFieldErrors } from './PlanInfoPage';
import { useCodelistOptions } from '../../hooks/useCodelistOptions';
import { applyPlanInfoFormToArealplan, toPlanInfoFormValues } from '../../mappers/toPlanInfo';
import type { PlanInfoFormValues } from '../../types';

type FormFelt = keyof PlanInfoFormValues;

/** Det vi bruker av en Zod-feil; pakken eksponerer feilene som `cause`. */
interface SchemaIssue {
    path: ReadonlyArray<string | number>;
    message: string;
}

interface FormState {
    /** Verdiene utkastet sist ble synket fra (server eller lagring). */
    baseline: PlanInfoFormValues;
    draft: PlanInfoFormValues;
}

/** Feltene brukeren har endret i `draft` mot `baseline`. */
function endredeFelter(state: FormState): FormFelt[] {
    return (Object.keys(state.baseline) as FormFelt[])
        .filter(k => state.draft[k] !== state.baseline[k]);
}

/** `beskrivelse` finnes ikke i domenet ennå og kan ikke lagres. */
const IKKE_LAGRET: ReadonlySet<FormFelt> = new Set(['beskrivelse']);

/**
 * Flytt utkastet over på en ny baseline: felter brukeren har endret mot
 * `fra`, og felter som ikke lagres, beholdes; resten tar verdien fra `til`.
 * Brukes når planen hentes på nytt mens skjemaet har ulagrede endringer, og
 * når en lagring er ferdig mens brukeren har fortsatt å skrive.
 */
function rebase(draft: PlanInfoFormValues, fra: PlanInfoFormValues, til: PlanInfoFormValues): PlanInfoFormValues {
    const next = { ...til };
    for (const k of Object.keys(til) as FormFelt[]) {
        if (IKKE_LAGRET.has(k) || draft[k] !== fra[k]) (next as Record<FormFelt, unknown>)[k] = draft[k];
    }
    return next;
}

function tilFeltfeil(issues: readonly SchemaIssue[]): PlanInfoFieldErrors {
    const next: PlanInfoFieldErrors = {};
    for (const issue of issues) {
        const key = issue.path[0] as FormFelt | undefined;
        if (key && !next[key]) next[key] = issue.message;
    }
    return next;
}

interface PlanInfoContainerProps {
    napId: string;
    /** Meldes når skjemaet får/mister ulagrede endringer, så navigasjon kan varsle. */
    onDirtyChange?: (dirty: boolean) => void;
}

const PlanInfoContainer: React.FC<PlanInfoContainerProps> = ({ napId, onDirtyChange }) => {
    const { t } = useTranslation();
    const toast = useToast();
    const { data: plan, isLoading, isError } = useArealplan(napId);
    const codelists = useCodelistOptions();
    const update = useUpdateArealplan();
    const [form, setForm] = useState<FormState | null>(null);
    const [fieldErrors, setFieldErrors] = useState<PlanInfoFieldErrors>({});
    const [confirmDiscard, setConfirmDiscard] = useState(false);
    const draft = form?.draft ?? null;

    // `beskrivelse` teller ikke: endringen kan ikke lagres, og vakten skal
    // ikke varsle om noe brukeren ikke kan gjøre noe med.
    const isDirty = useMemo(
        () => (form ? endredeFelter(form).some(k => !IKKE_LAGRET.has(k)) : false),
        [form]
    );

    // Ny serverversjon (refetch eller lagring): felter brukeren har endret,
    // beholdes, resten oppdateres. Slik blir ikke en samtidig endring fra en
    // annen saksbehandler skrevet over med en gammel verdi.
    useEffect(() => {
        if (!plan) return;
        const values = toPlanInfoFormValues(plan);
        setForm(f => (f
            ? { baseline: values, draft: rebase(f.draft, f.baseline, values) }
            : { baseline: values, draft: values }));
    }, [plan]);

    useEffect(() => {
        onDirtyChange?.(isDirty);
    }, [isDirty, onDirtyChange]);

    useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

    const handleChange = useCallback(
        <K extends FormFelt>(field: K, value: PlanInfoFormValues[K]) => {
            setForm(f => (f ? { ...f, draft: { ...f.draft, [field]: value } } : f));
            // Rydd feltets feilmelding så snart brukeren retter det.
            setFieldErrors(e => (e[field] ? { ...e, [field]: undefined } : e));
        },
        []
    );

    const handleSave = useCallback(async () => {
        if (!plan || !form) return;
        const submitted = form.draft;
        const updated = applyPlanInfoFormToArealplan(plan, submitted);

        // Samme schema som use-casen validerer mot, og som der bare for de
        // endrede feltene, så eldre planer kan lagres. Gir feltnære feil før
        // innsending.
        const shape = arealplanEndringSchema.shape;
        const maske = Object.fromEntries(
            endredeFelter(form).filter(k => k in shape).map(k => [k, true])
        ) as Partial<Record<keyof typeof shape, true>>;
        const parsed = arealplanEndringSchema.pick(maske).safeParse(updated);
        if (!parsed.success) {
            setFieldErrors(tilFeltfeil(parsed.error.issues));
            return;
        }
        setFieldErrors({});

        const saved = await toast.execute(
            async () => {
                try {
                    return await update.mutateAsync(updated);
                } catch (err) {
                    // Use-casen validerer på nytt mot lagret plan; vis feilen
                    // ved feltet og med pakkens melding i stedet for den generelle.
                    if (err instanceof ValidationError) {
                        const cause = (err as Error & { cause?: unknown }).cause as { issues?: SchemaIssue[] } | undefined;
                        if (cause?.issues) setFieldErrors(tilFeltfeil(cause.issues));
                        throw err;
                    }
                    throw new Error(t('common.errorSaving'));
                }
            },
            { success: t('common.saved') }
        );
        if (saved) {
            // Det brukeren skrev mens lagringen pågikk, er endret mot
            // `submitted` og beholdes; resten tar de lagrede verdiene.
            const values = toPlanInfoFormValues(saved);
            setForm(f => (f ? { baseline: values, draft: rebase(f.draft, submitted, values) } : f));
        }
    }, [toast, t, update, plan, form]);

    // Forkast synker mot siste serverdata. Bekreftes først, siden endringene
    // ellers forsvinner på ett klikk.
    const handleDiscard = useCallback(() => {
        setConfirmDiscard(false);
        if (!plan) return;
        const values = toPlanInfoFormValues(plan);
        setForm({ baseline: values, draft: values });
        setFieldErrors({});
    }, [plan]);

    if (isLoading || !draft || codelists.isLoading) {
        return <PlanDetailSkeleton title={t('planInfo.title')} subtitle={t('planInfo.subtitle')} variant="form" />;
    }
    if (isError) {
        return <Notice variant="danger">{t('common.errorLoading')}</Notice>;
    }
    if (!plan) {
        return <Notice variant="info">{t('plan.notFound')}</Notice>;
    }

    return (
        <>
            <PlanInfoPage
                values={draft}
                fieldErrors={fieldErrors}
                plantypeOptions={codelists.plantype}
                planstatusOptions={codelists.planstatus}
                lovreferanseOptions={codelists.lovreferanse}
                forslagsstillerTypeOptions={codelists.forslagsstillerType}
                onChange={handleChange}
                onCancel={() => setConfirmDiscard(true)}
                onSave={handleSave}
                isDirty={isDirty}
                isSaving={update.isPending}
            />
            <UnsavedChangesDialog
                open={confirmDiscard}
                onStay={() => setConfirmDiscard(false)}
                onDiscard={handleDiscard}
            />
        </>
    );
};

export default PlanInfoContainer;
